/**
 * SAVINGS PAGE (Page 1) - Human Only
 *
 * GROUND RULES:
 * - NO acronyms (EPC, SEDBUK, SCOP, SPF, kWh)
 * - NO percentages
 * - NO technical jargon
 * - Maximum 2-sentence explanations
 * - Anyone can understand without help
 *
 * This component NEVER recalculates. It only displays pre-computed values.
 */

// ==================== TYPES ====================

import { formatCurrency } from '../utils';

export interface SavingsPageProps {
  baseline: {
    costGbp: number;
    energyKwh: number;
    carbonKg: number;
  };
  option: {
    label: string;
    costGbp: number;
    energyKwh: number;
    carbonKg: number;
  };
}

// ==================== COMPONENT ====================

export function SavingsPage({ baseline, option }: SavingsPageProps) {
  // Calculate savings (display only, not business logic)
  const costSaving = baseline.costGbp - option.costGbp;
  const energySaving = baseline.energyKwh - option.energyKwh;
  const carbonSaving = baseline.carbonKg - option.carbonKg;
  const carbonSavingTonnes = carbonSaving / 1000;

  // Format numbers for display
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const formatDecimal = (num: number, decimals: number = 1) => {
    return new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  };

  return (
    <div className="savings-page">
      {/* Hero Comparison */}
      <div className="hero-comparison">
        <h1>Running Costs</h1>
        <div className="cost-comparison">
          <div className="cost-before">
            <div className="cost-label">Current system</div>
            <div className="cost-value">{formatCurrency(baseline.costGbp)} / year</div>
          </div>
          <div className="cost-arrow">→</div>
          <div className="cost-after">
            <div className="cost-label">{option.label}</div>
            <div className="cost-value">{formatCurrency(option.costGbp)} / year</div>
          </div>
        </div>
        <div className="comparison-note">
          <p>
            <strong>Compared to your current system:</strong>{' '}
            {costSaving > 0
              ? `${formatCurrency(costSaving)} less per year`
              : costSaving < 0
              ? `${formatCurrency(Math.abs(costSaving))} more per year`
              : 'Similar running cost'}
          </p>
        </div>
      </div>

      {/* Energy Metrics - Clear distinction between delivered heat and fuel input */}
      <div className="impact-summary">
        <div className="impact-line impact-line-note">
          <span className="impact-label-full">
            Total heat delivered remains the same. Energy input differs due to system efficiency.
          </span>
        </div>
        <div className="impact-line">
          <span className="impact-label">Fuel/electricity input:</span>
          <span className="impact-values">
            {formatNumber(baseline.energyKwh)} → {formatNumber(option.energyKwh)} kWh
          </span>
        </div>
        <div className="impact-line">
          <span className="impact-label">Carbon footprint:</span>
          <span className="impact-values">
            {formatDecimal(baseline.carbonKg / 1000)} → {formatDecimal(option.carbonKg / 1000)} tonnes
          </span>
        </div>
      </div>

      {/* Two Stacked Bars */}
      <div className="breakdown-bars">
        <h2>What makes up your heating cost</h2>
        <div className="bar-row">
          <div className="bar-label">Current</div>
          <div className="stacked-bar">
            <div className="bar-segment heating" style={{ width: '80%' }}>
              <span className="bar-segment-label">Heating</span>
            </div>
            <div className="bar-segment water" style={{ width: '20%' }}>
              <span className="bar-segment-label">Hot water</span>
            </div>
          </div>
          <div className="bar-total">{formatCurrency(baseline.costGbp)}</div>
        </div>
        <div className="bar-row">
          <div className="bar-label">{option.label}</div>
          <div className="stacked-bar">
            <div className="bar-segment heating" style={{ width: '80%' }}>
              <span className="bar-segment-label">Heating</span>
            </div>
            <div className="bar-segment water" style={{ width: '20%' }}>
              <span className="bar-segment-label">Hot water</span>
            </div>
          </div>
          <div className="bar-total">{formatCurrency(option.costGbp)}</div>
        </div>
      </div>

      {/* 3-Bullet Summary */}
      <div className="summary-bullets">
        <h3>What this means</h3>
        <ul>
          <li>
            <strong>Running cost</strong> — {costSaving > 0
              ? `This option costs around ${formatCurrency(costSaving)} less per year than your current system.`
              : costSaving < 0
              ? `This option costs around ${formatCurrency(Math.abs(costSaving))} more per year than your current system.`
              : 'Running costs are similar to your current system.'}
          </li>
          <li>
            <strong>Energy input</strong> — {energySaving > 0
              ? `Requires ${formatNumber(energySaving)} kWh less fuel/electricity per year.`
              : energySaving < 0
              ? `Requires ${formatNumber(Math.abs(energySaving))} kWh more fuel/electricity per year.`
              : 'Similar energy input to your current system.'}
          </li>
          <li>
            <strong>Carbon footprint</strong> — {carbonSavingTonnes > 0.1
              ? `Reduces carbon by ${formatDecimal(carbonSavingTonnes)} tonnes per year.`
              : carbonSavingTonnes < -0.1
              ? `Increases carbon by ${formatDecimal(Math.abs(carbonSavingTonnes))} tonnes per year.`
              : 'Similar carbon footprint to your current system.'}
          </li>
        </ul>
      </div>

      {/* Single Honest Line */}
      <div className="disclaimer">
        <p>
          <em>Typical first-year estimate based on today's prices.</em>
        </p>
      </div>

      <style>{`
        .savings-page {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
          max-width: 800px;
          margin: 0 auto;
          padding: 2rem;
          line-height: 1.6;
        }

        .hero-comparison {
          text-align: center;
          margin-bottom: 3rem;
          padding: 2rem;
          background: white;
          color: #1e293b;
          border-radius: 12px;
          border: 2px solid #e2e8f0;
          border-left: 4px solid #2563eb;
        }

        .hero-comparison h1 {
          margin: 0 0 1.5rem 0;
          font-size: 2.5rem;
          font-weight: 700;
        }

        .cost-comparison {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
        }

        .cost-before,
        .cost-after {
          background: #f8fafc;
          padding: 1rem 1.5rem;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .cost-label {
          font-size: 0.875rem;
          color: #64748b;
          margin-bottom: 0.25rem;
        }

        .cost-value {
          font-size: 1.75rem;
          font-weight: 700;
          color: #1e293b;
        }

        .cost-arrow {
          font-size: 2rem;
          font-weight: 700;
          color: #2563eb;
        }

        .comparison-note {
          font-size: 1rem;
          padding: 0.75rem 1rem;
          background: #f8fafc;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          color: #475569;
        }

        .comparison-note p {
          margin: 0;
        }

        .impact-summary {
          margin-bottom: 2rem;
          padding: 1.5rem;
          background: #f7fafc;
          border-radius: 8px;
        }

        .impact-line {
          display: flex;
          justify-content: space-between;
          padding: 0.75rem 0;
          border-bottom: 1px solid #e2e8f0;
        }

        .impact-line:last-child {
          border-bottom: none;
        }

        .impact-label {
          font-weight: 600;
          color: #2d3748;
        }

        .impact-label-full {
          font-size: 0.875rem;
          color: #64748b;
          font-style: italic;
        }

        .impact-line-note {
          justify-content: center;
          border-bottom: none;
          padding-bottom: 0;
        }

        .impact-values {
          color: #4a5568;
          font-weight: 500;
        }

        .breakdown-bars {
          margin-bottom: 2rem;
        }

        .breakdown-bars h2 {
          font-size: 1.5rem;
          margin-bottom: 1rem;
          color: #2d3748;
        }

        .bar-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .bar-label {
          width: 120px;
          font-weight: 600;
          color: #4a5568;
          font-size: 0.875rem;
        }

        .stacked-bar {
          flex: 1;
          display: flex;
          height: 40px;
          border-radius: 4px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .bar-segment {
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 0.75rem;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .bar-segment.heating {
          background: #4299e1;
        }

        .bar-segment.water {
          background: #48bb78;
        }

        .bar-segment-label {
          opacity: 0.9;
        }

        .bar-total {
          width: 100px;
          text-align: right;
          font-weight: 700;
          color: #2d3748;
        }

        .summary-bullets {
          margin-bottom: 2rem;
        }

        .summary-bullets h3 {
          font-size: 1.25rem;
          margin-bottom: 1rem;
          color: #2d3748;
        }

        .summary-bullets ul {
          list-style: none;
          padding: 0;
        }

        .summary-bullets li {
          padding: 0.75rem 0;
          padding-left: 1.5rem;
          position: relative;
          color: #4a5568;
        }

        .summary-bullets li::before {
          content: '✓';
          position: absolute;
          left: 0;
          color: #48bb78;
          font-weight: 700;
          font-size: 1.25rem;
        }

        .disclaimer {
          text-align: center;
          padding: 1rem;
          background: #edf2f7;
          border-radius: 8px;
          color: #4a5568;
        }

        .disclaimer p {
          margin: 0;
        }

        @media (max-width: 640px) {
          .savings-page {
            padding: 1rem;
          }

          .hero-comparison h1 {
            font-size: 1.75rem;
          }

          .cost-comparison {
            flex-direction: column;
          }

          .cost-arrow {
            transform: rotate(90deg);
          }

          .bar-row {
            flex-direction: column;
            align-items: flex-start;
          }

          .bar-label,
          .bar-total {
            width: 100%;
          }

          .stacked-bar {
            width: 100%;
          }
        }

        @media print { 
          .savings-page { 
            page-break-after: always; 
          }
        }
      `}</style>
    </div>
  );
}

export default SavingsPage;
